/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    AddOrderTemplateToCartComponent,
    ILinesAddedToCartDialogResources,
    IOrderTemplateNameDialogResources,
    RenameOrderTemplateComponent,
    OrderTemplateNameDialog
} from '@msdyn365-commerce/components';
import { IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import { ProductList } from '@msdyn365-commerce/retail-proxy';
import { OrderTemplate, Random } from '@msdyn365-commerce-modules/retail-actions';
import { Button, ITelemetryContent, Popover } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { useState } from 'react';
import { ShareOrderTemplateDialog, IShareOrderTemplateDialogResources } from './order-template-share-modal';

export interface IOrderTemplateActionsData {
    orderTemplate: OrderTemplate;
}

export interface IOrderTemplateActionsProps extends IComponentProps<IOrderTemplateActionsData> {
    context: ICoreContext;
    telemetryContent?: ITelemetryContent;
    handlers: {
        onRemoveTemplate(orderTemplateId: string): void;
        onRenameTemplate(orderTemplate: ProductList): Promise<void>;
        onShareTemplate?(orderTemplate: ProductList): Promise<void>;
    };
    renameOrderTemplateDialogStrings: IOrderTemplateNameDialogResources;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    isShared?: boolean;
    isB2bAdmin?: boolean;
    showOptionsLabel?: boolean;
    isB2bContact?: boolean;
    resources: {
        addTemplateToBagButtonText: string;
        renameTemplateButtonText: string;
        deleteTemplateButtonText: string;
        closeWindowButtonText: string;
        addToCartFailureMessage: string;
        addToCartSuccessMessage: string;
        addToCartProcessMessage: string;
        orderTemplateErrorMessage: string;
        linesAddedToCartDialogResources: ILinesAddedToCartDialogResources;
        templateShareButtonText?: string;
        templateUnshareButtonText?: string;
        ShareOrderTemplateDialog?: IShareOrderTemplateDialogResources;
    };
}

export interface IOrderTemplateActionsViewProps {
    addTemplateToBag: React.ReactNode;
    renameTemplate?: React.ReactNode;
    removeTemplate?: React.ReactNode;
    isMultipleCatalogsFeatureEnabled?: boolean;
    actionsSplitButton?: React.ReactNode;
}

/**
 * Remove Order Action.
 * @param props -Order template action props.
 * @returns Remove order template.
 */
const removeOrderActionHandler = (props: IOrderTemplateActionsProps) => () => {
    _removeOrderTemplate(props);
};

export const OrderTemplateActionsView = (props: IOrderTemplateActionsProps): IOrderTemplateActionsViewProps => {
    const { resources, isMultipleCatalogFeatureEnabledInHq } = props;
    const { deleteTemplateButtonText } = resources;

    return {
        addTemplateToBag: _getOrderTemplateAddToCartDialog(props),
        renameTemplate: !isMultipleCatalogFeatureEnabledInHq && _getOrderTemplateRenameControl(props),
        removeTemplate: !isMultipleCatalogFeatureEnabledInHq && (
            <Button className='ms-order-template-action-bar__delete-template-button' onClick={removeOrderActionHandler(props)}>
                {deleteTemplateButtonText}
            </Button>
        ),
        actionsSplitButton: isMultipleCatalogFeatureEnabledInHq && <ActionsSplitButton {...props} showOptionsLabel={true} />
    };
};

const _removeOrderTemplate = (props: IOrderTemplateActionsProps): void => {
    const {
        data: { orderTemplate },
        handlers
    } = props;
    const { onRemoveTemplate } = handlers;

    if (onRemoveTemplate) {
        onRemoveTemplate(orderTemplate.productList.Id);
    }
};

const _getOrderTemplateAddToCartDialog = (props: IOrderTemplateActionsProps): React.ReactNode => {
    const {
        data: { orderTemplate },
        typeName,
        id,
        context,
        telemetryContent,
        resources,
        isMultipleCatalogFeatureEnabledInHq
    } = props;
    const {
        addTemplateToBagButtonText,
        closeWindowButtonText,
        addToCartFailureMessage,
        addToCartSuccessMessage,
        addToCartProcessMessage,
        orderTemplateErrorMessage,
        linesAddedToCartDialogResources
    } = resources;

    const dialogStrings = {
        closeWindowButtonText,
        addToCartFailureMessage,
        addToCartSuccessMessage,
        addToCartProcessMessage,
        orderTemplateErrorMessage,
        linesAddedToCartDialogResources
    };

    return (
        <AddOrderTemplateToCartComponent
            context={context}
            id={id}
            typeName={typeName}
            data={{ orderTemplate }}
            className=''
            addToCartText={addTemplateToBagButtonText}
            dialogStrings={dialogStrings}
            telemetryContent={telemetryContent}
            isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
        />
    );
};

const _getOrderTemplateRenameControl = (props: IOrderTemplateActionsProps): React.ReactNode => {
    const { renameOrderTemplateDialogStrings, resources, context, handlers } = props;
    return (
        <RenameOrderTemplateComponent
            onRenameTemplate={handlers.onRenameTemplate}
            className='msc-order-template-name-dialog'
            context={context}
            id={props.id}
            data={{ orderTemplate: props.data.orderTemplate.productList }}
            typeName={props.typeName}
            renameOrderTemplateDialogStrings={renameOrderTemplateDialogStrings}
            renameOrderTemplateButtonText={resources.renameTemplateButtonText}
            isMultipleCatalogFeatureEnabledInHq
        />
    );
};

export const ActionsSplitButton: React.FC<IOrderTemplateActionsProps> = (props: IOrderTemplateActionsProps) => {
    const [isStatementRequestAdminDropdownOpen, setIsStatementRequestAdminDropdownOpen] = useState(false);
    const [renameOrderTemplateDialogOpen, setOrderTemplateNameDialogOpen] = React.useState(false);
    const [isShareOrderTemplateDialogOpen, setShareOrderTemplateDialogOpen] = React.useState(false);

    const isShared = props.isShared ?? false;
    const showOptionsLabel = props.showOptionsLabel ?? false;

    const onOrderTemplateNameCancel = () => {
        context.telemetry.debug('Rename of template was canceled.');
        buttonEventReference.current?.focus();
    };

    const buttonEventReference = React.useRef<HTMLButtonElement | null>(null);

    const onRenameButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;
        setOrderTemplateNameDialogOpen(true);
        event.preventDefault();
    };

    const onShareButtonClcik = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;
        setShareOrderTemplateDialogOpen(true);
        event.preventDefault();
    };

    const onToggleDropdown1 = () => {
        setIsStatementRequestAdminDropdownOpen(!isStatementRequestAdminDropdownOpen);
    };
    const {
        data: { orderTemplate },
        resources,
        renameOrderTemplateDialogStrings,
        context
    } = props;

    const className = 'ms-order-template-action-bar';

    const popoverReference = React.createRef<HTMLButtonElement>();
    const renderModalPopup = (
        <OrderTemplateNameDialog
            existingOrderTemplate={orderTemplate.productList}
            isUpdateDialog
            className='msc-order-template-name-dialog'
            modalOpen={renameOrderTemplateDialogOpen}
            setModalOpen={setOrderTemplateNameDialogOpen}
            context={context}
            id={props.id}
            data={props.data}
            orderTemplateName={props.data.orderTemplate.productList.Name}
            typeName={props.typeName}
            dialogStrings={renameOrderTemplateDialogStrings}
            onCancel={onOrderTemplateNameCancel}
            onSuccess={props.handlers.onRenameTemplate}
        />
    );
    const requestStatementDropdownString = `request-statement-dropdown-id-${Random.Guid.generateGuid()}`;

    return (
        <div className={`${className}__dropdown`}>
            {renderModalPopup}
            {resources.ShareOrderTemplateDialog && props.handlers.onShareTemplate && (
                <ShareOrderTemplateDialog
                    className='msc-share-order-template-dialog'
                    modalOpen={isShareOrderTemplateDialogOpen}
                    setModalOpen={setShareOrderTemplateDialogOpen}
                    isShared={isShared}
                    productList={orderTemplate.productList}
                    context={context}
                    id={props.id}
                    data={props.data}
                    typeName={props.typeName}
                    onSuccess={props.handlers.onShareTemplate}
                    resources={resources.ShareOrderTemplateDialog}
                />
            )}
            {showOptionsLabel ? (
                <Button
                    className={
                        isStatementRequestAdminDropdownOpen
                            ? `${className}__action__requestStatement__toggleUp`
                            : `${className}__action__requestStatement__toggleDown`
                    }
                    onClick={onToggleDropdown1}
                    innerRef={popoverReference}
                    aria-haspopup='true'
                    aria-expanded={isStatementRequestAdminDropdownOpen}
                    id={requestStatementDropdownString}
                >
                    <span className={`${className}__action__requestStatement__buttonLabel`}>{'Options'}</span>
                </Button>
            ) : (
                <Button
                    className={
                        isStatementRequestAdminDropdownOpen
                            ? 'ms-order-template__list-options-toggle ms-order-template__list-options-toggle--open'
                            : 'ms-order-template__list-options-toggle'
                    }
                    onClick={onToggleDropdown1}
                    innerRef={popoverReference}
                    aria-haspopup='true'
                    aria-expanded={isStatementRequestAdminDropdownOpen}
                />
            )}
            <Popover
                aria-labelledby={requestStatementDropdownString}
                className={`${className}__action__requestStatement__dropdownVisible`}
                isOpen={isStatementRequestAdminDropdownOpen}
                placement='bottom'
                target={popoverReference}
                toggle={onToggleDropdown1}
                role='listbox'
                aria-hidden={!isStatementRequestAdminDropdownOpen}
            >
                <Button className={`${className}__action__requestStatement__dropdownOption rename`} onClick={onRenameButtonClick}>
                    {resources.renameTemplateButtonText}
                </Button>

                {(props.isB2bContact ?? false) && (props.isB2bAdmin ?? false) && (
                    <Button
                        className={`${className}__action__requestStatement__dropdownOption share`}
                        onClick={onShareButtonClcik}
                        role='option'
                    >
                        {isShared ? resources.templateUnshareButtonText : resources.templateShareButtonText}
                    </Button>
                )}

                <Button
                    onClick={removeOrderActionHandler(props)}
                    className={`${className}__action__requestStatement__dropdownOption delete`}
                    role='option'
                >
                    {'Delete'}
                </Button>
            </Popover>
        </div>
    );
};
